// What the pill is saying, as arithmetic. The pure half of `RefusalPill.tsx`
// (PLAN-UI §28.3's bottom-centre row, §28.9; screen-mock `#pill`).
//
// **A refusal is said out loud, and the pill is where it is said** — the
// standing rule (`SessionView.refusal` carries the sentence precisely so
// no click is a silent no-op) inherited by §28.6. It was drawn by
// `ShelfStrip`'s caption line at 340px with an ellipsis, which is half a
// reason, which is the silent no-op wearing a costume. `HintPill` is the
// surface and never truncates; this decides when it speaks.
//
// **The shape is `hoverState.ts`'s**: a state and a step function over events,
// no React and no timers of its own. Everything a timed announcement gets
// wrong is an edge — the same sentence refused twice in a row, a tool picked
// up while a refusal is still standing, a stale timer firing over a line that
// has since been replaced — and each of those is one branch here with a test
// under it, rather than four `setTimeout`s in a component.
//
// **`said` is why a refusal carries a sequence number rather than just a
// string.** Clicking the same illegal thing twice is two answers to two
// questions, and comparing text cannot tell them apart: the second click would
// leave the pill mid-fade with nothing to say the game had heard it. The hook
// bumps a counter on every non-null `setRefusal`, and a bump is the whole
// event — the text may be identical and the clock still restarts.
//
// **`spoke` is the same argument made for the animation.** `said` identifies an
// announcement for the *clock*; the entrance pop needs an identity of its own
// because a tool sentence has no counter and because half the events that
// arrive here raise nothing — a first tool, a tool swallowed under a standing
// refusal, a tick. So it counts the lines actually *raised*, and
// `RefusalPill.tsx` keys the surface on it, which is what makes React remount
// and replay the keyframe when the very same sentence is refused twice.

/** How long a refusal stands before it goes quiet. The mock's `say()`. */
export const REFUSAL_MS = 2400;

/**
 * How long a tool's drag sentence stands.
 *
 * The same window on purpose: they are one line taking turns, and two lengths
 * would read as two different mechanisms sharing a pill.
 */
export const TOOL_MS = 2400;

/**
 * How long a notice stands (§29 U5).
 *
 * Longer than the other two, and the difference is who asked. A refusal and a
 * tool sentence answer something the player just did, so they are read the
 * moment they appear; a notice arrives unbidden, on a screen that has just
 * changed under them, and 2400ms is gone before anybody has looked down.
 */
export const NOTICE_MS = 6000;

export type PillEvent =
  /**
   * A refusal was said. `said` is the hook's counter — a bump is the event, so
   * the same `text` at the same `said` is the same announcement and does
   * nothing, and the same `text` at a *new* `said` restarts the clock.
   */
  | { kind: "refusal"; text: string; said: number; now: number }
  /**
   * The live drag tool changed, and its sentence with it. `tool` is the
   * identity — the pill announces a *change*, so the first one it ever hears
   * only records what the tool is.
   */
  | { kind: "tool"; tool: string; text: string; now: number }
  /**
   * The game has something to say that nobody clicked for (§29 U5) — today,
   * that a continued run's history could not be brought back.
   *
   * **Said once, and the text is the identity.** There is no counter because
   * there is no second question: a notice is a fact about the session, raised
   * by a state that is set once, and every render after it hands the same
   * sentence back. Not a refusal — nothing was turned away — so it is not rust.
   */
  | { kind: "notice"; text: string; now: number }
  /** The window may have run out. Fired by the component's one timer. */
  | { kind: "tick"; now: number };

export interface PillState {
  /** What is on screen, or null for quiet. */
  text: string | null;
  /** The refusal variant — rust. False for a tool sentence. */
  bad: boolean;
  /** When the line goes quiet, in the same clock the events carry. */
  until: number;
  /** The refusal counter this line was raised by. 0 is "none yet". */
  said: number;
  /** The tool last heard about, or null before the first one. */
  tool: string | null;
  /**
   * How many lines have actually been raised. 0 is "nothing said yet".
   *
   * The identity of an *announcement*, where `said` is the identity of a
   * refusal: it bumps for a tool sentence too, and it does not bump for the
   * events that change the state without putting words on the screen — the
   * first tool, a tool recorded under a standing refusal, a tick. The
   * component keys the surface on it so the entrance replays exactly when
   * something new is said.
   */
  spoke: number;
  /** The notice already said, or null. Its own latch — see the event. */
  notice: string | null;
}

/** Nothing said. What the pill is on mount, and what it returns to. */
export const QUIET: PillState = {
  text: null,
  bad: false,
  until: 0,
  said: 0,
  tool: null,
  spoke: 0,
  notice: null
};

export function pillStep(state: PillState, event: PillEvent): PillState {
  switch (event.kind) {
    case "refusal":
      // The same announcement arriving again — a re-render, not a second
      // click. Identity is the counter and never the text.
      if (event.said === state.said) return state;
      return {
        ...state,
        text: event.text,
        bad: true,
        until: event.now + REFUSAL_MS,
        said: event.said,
        spoke: state.spoke + 1
      };

    case "tool": {
      if (event.tool === state.tool) return state;
      // **Nothing is said on mount.** The tool a game opens holding was not
      // chosen, so announcing it would be the interface talking first, and the
      // rail's own hover cards are where the sentence lives when nobody asked.
      const first = state.tool === null;
      // **A refusal or a notice outranks a tool line while it stands.** A
      // refusal answers something the player just tried and a notice is the
      // game speaking; a drag sentence describes something they might. The
      // tool is still recorded, so the line does not surface late once the
      // other has faded — a sentence about a tool picked up three seconds ago
      // is a sentence about nothing.
      const standing =
        state.text !== null &&
        event.now < state.until &&
        (state.bad || state.text === state.notice);
      if (first || standing) return { ...state, tool: event.tool };
      return {
        ...state,
        text: event.text,
        bad: false,
        until: event.now + TOOL_MS,
        tool: event.tool,
        spoke: state.spoke + 1
      };
    }

    case "notice":
      // Already said. The state is set once and handed back on every render,
      // so this is the ordinary case rather than the edge.
      if (event.text === state.notice) return state;
      return {
        ...state,
        text: event.text,
        bad: false,
        until: event.now + NOTICE_MS,
        notice: event.text,
        spoke: state.spoke + 1
      };

    case "tick":
      // A stale fire must not blank a line raised since it was scheduled, so
      // the window is re-read rather than trusted.
      if (state.text === null || event.now < state.until) return state;
      return { ...state, text: null, bad: false, until: 0 };
  }
}

/** The fold. What a sequence of events leaves the pill saying. */
export function pillState(events: readonly PillEvent[]): PillState {
  return events.reduce(pillStep, QUIET);
}
